import 'katex/dist/katex.min.css';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/styles.css';
import '../styles/animation/toast.css';
import 'react-18-image-lightbox/style.css';
import 'react-calendar/dist/Calendar.css';
import 'cropperjs/dist/cropper.min.css';
import '../styles/calendar.css';

import { BaseStyles, Button, SSRProvider, ThemeProvider, useTheme } from '@primer/react';
import type { NextPage as BaseNextPage } from 'next';
import { SessionProvider, signOut } from 'next-auth/react';
import type { AppProps } from 'next/app';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import { ToastContainer } from 'react-toastify';

import ErrorBoundary from '../components/core/ErrorBoundary';
import ErrorState from '../components/core/ErrorState';
import { AuthenticatedRoute, UnauthenticatedRoute, PermissionRoute, RouteType } from '../components/core/Route';
import View from '../components/core/View';
import { PRIMER_OVERLAY_ZINDEX } from '../constants/zIndexes';
import { ColorModeContextProvider } from '../contexts/ColorModeContext';
import { PaginationContextProvider } from '../contexts/PaginationContext';
import { ToastTransition } from '../hooks/useToast';
import RelayEnvironment from '../relay/environment';

type PageWithLayout = { getLayout?: (page: ReactElement) => ReactNode };
type PageWithAuthenticated = { authenticated: boolean };
type PageWithRoutes = { routes: RouteType[] };

export type NextPage<P = {}, IP = P> = BaseNextPage<P, IP> & PageWithLayout & PageWithAuthenticated & PageWithRoutes;

type AppPropsWithLayout = AppProps & {
  Component: NextPage;
};

const reLogin = () => {
  try {
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch {
    // 저장소 접근이 막힌 브라우저(사생활 보호 모드 등)여도 로그아웃은 한다
  }
  signOut({ callbackUrl: '/login' });
};

const MyApp = ({ Component, pageProps, router }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <SSRProvider>
      <ColorModeContextProvider>
        {({ colorMode }) => (
          <ThemeProvider colorMode={colorMode} preventSSRMismatch>
            <CssThemeWrapper>
              <BaseStyles>
                <SessionProvider session={pageProps.session}>
                  <ErrorBoundary
                    fallback={
                      <View
                        sx={{
                          width: '100vw',
                          height: '100vh',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <View sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <ErrorState description={'잠시 후에 다시 시도해 주세요'} />
                          <View sx={{ display: 'flex', gap: 2 }}>
                            <Button onClick={() => window.location.reload()}>다시 시도</Button>
                            {/* 로그인 세션이 무효가 되면(서버 키 교체 등) 이 화면에서 나갈 길이 없었다 — 세션을 지우고 로그인 화면으로 */}
                            <Button variant={'primary'} onClick={reLogin}>
                              다시 로그인
                            </Button>
                          </View>
                        </View>
                      </View>
                    }
                  >
                    <PaginationContextProvider>
                      <RelayEnvironmentProvider environment={RelayEnvironment}>
                        {Component.authenticated ? (
                          <AuthenticatedRoute>
                            <PermissionRoute router={router}>{getLayout(<Component {...pageProps} />)}</PermissionRoute>
                          </AuthenticatedRoute>
                        ) : (
                          <UnauthenticatedRoute>{getLayout(<Component {...pageProps} />)}</UnauthenticatedRoute>
                        )}
                        <div id="dialog_root" />
                        <div
                          id="__primerPortalRoot__"
                          style={{ position: 'absolute', zIndex: PRIMER_OVERLAY_ZINDEX }}
                        />
                        <ToastContainer
                          hideProgressBar={true}
                          autoClose={2500}
                          position={'top-center'}
                          transition={ToastTransition}
                          closeButton={false}
                          style={{ width: 'auto', top: '160px', left: '50%', transform: 'translateX(-50%)' }}
                          toastStyle={{ backgroundColor: 'transparent', padding: 0, minHeight: 'auto' }}
                          bodyStyle={{ padding: 0 }}
                          pauseOnFocusLoss={false}
                        />
                        <GlobalTheme />
                      </RelayEnvironmentProvider>
                    </PaginationContextProvider>
                  </ErrorBoundary>
                </SessionProvider>
              </BaseStyles>
            </CssThemeWrapper>
          </ThemeProvider>
        )}
      </ColorModeContextProvider>
    </SSRProvider>
  );
};

const CssThemeWrapper = ({ children }: PropsWithChildren) => {
  const { resolvedColorScheme } = useTheme();

  return (
    <div data-color-mode={resolvedColorScheme} data-dark-theme={'dark'} data-light-theme={'light'}>
      {children}
    </div>
  );
};

const GlobalTheme = () => {
  const { theme } = useTheme();
  return (
    <style jsx global>{`
      html,
      body {
        background-color: ${theme?.colors.canvas.default};
      }
      :any-link {
        color: inherit;
        font-weight: inherit;
        text-decoration: none;
      }
      fieldset {
        padding: 0;
        margin: 0;
      }
      input[type='checkbox'],
      input[type='radio'] {
        width: 20px;
        height: 20px;
        margin: 3px 0;
      }
      div:has(> input[type='checkbox'], > input[type='radio']) + div {
        justify-content: center;
      }
      div:has(> input[type='checkbox'], > input[type='radio']) {
        display: flex;
        align-items: center;
      }

      .sketch-picker {
        background: ${theme?.colors.canvas.overlay} !important;
      }
      .sketch-picker label {
        color: ${theme?.colors.fg.default} !important;
      }
      .cropper-modal {
        opacity: 0.3;
      }
      .cropper-line {
        padding: 10px;
      }
    `}</style>
  );
};

export default MyApp;
